// ============================================
// Core Type Definitions for JLPT Flashcard App
// ============================================

// JLPT Levels
export type JLPTLevel = 'N5' | 'N4' | 'N3' | 'Everyday' | 'Hiragana' | 'Katakana';

export const KANA_LEVELS: readonly JLPTLevel[] = ['Hiragana', 'Katakana'] as const;

export function isKanaLevel(level: JLPTLevel): level is 'Hiragana' | 'Katakana' {
  return level === 'Hiragana' || level === 'Katakana';
}

// Application modes
export type Mode = 'study' | 'quiz';
export type QuizMode =
  | 'multiple-choice'
  | 'listening-first'
  | 'mixed-challenge'
  | 'speed-challenge'
  | 'kanji-only'
  | 'particle-quiz'
  | 'grammar-quiz';

export const KANA_QUIZ_MODES: readonly QuizMode[] = [
  'multiple-choice',
  'listening-first',
  'speed-challenge'
] as const;

// Vocabulary-driven quiz modes — available at every non-kana level. The
// fill-in-the-blank modes (particle / grammar) are level-specific and layered
// on by availableQuizModesForLevel below.
export const VOCAB_QUIZ_MODES: readonly QuizMode[] = [
  'multiple-choice',
  'listening-first',
  'mixed-challenge',
  'speed-challenge',
  'kanji-only'
] as const;

/**
 * Quiz modes offered for a level. Kana levels get their reduced set; every
 * other level gets the vocab modes plus its own fill-blank mode: N5/N4 have
 * particle data, N3 has grammar patterns, and Everyday (vocab only) gets
 * neither — no cross-level fallback content is offered.
 */
export function availableQuizModesForLevel(level: JLPTLevel): QuizMode[] {
  if (isKanaLevel(level)) return [...KANA_QUIZ_MODES];
  const modes: QuizMode[] = [...VOCAB_QUIZ_MODES];
  if (level === 'N5' || level === 'N4') modes.push('particle-quiz');
  else if (level === 'N3') modes.push('grammar-quiz');
  return modes;
}

// Word types
export type WordType = 'noun' | 'verb' | 'i-adjective' | 'na-adjective' | 'kana';

// Kana-specific extension to Word
export type KanaGroup = 'base' | 'dakuten' | 'handakuten' | 'yoon';

// SRS States - progression through spaced repetition
export type SRSState =
  | 'new'
  | 'learning_1'
  | 'learning_2'
  | 'review_1'
  | 'review_2'
  | 'review_3'
  | 'mastered';

// SRS State Categories for UI display
export type SRSCategory = 'new' | 'learning' | 'review' | 'mastered';

// ============================================
// Vocabulary & Word Types
// ============================================

export interface Word {
  japanese: string;
  reading: string;
  meaning: string;
  type: WordType;
  sourceLevel?: JLPTLevel;
}

export interface KanaWord extends Word {
  type: 'kana';
  row: string;       // 'a' | 'ka' | 'sa' | ... — for row-filter UX later
  column: string;    // 'a' | 'i' | 'u' | 'e' | 'o' (or 'ya'/'yu'/'yo' for yoon)
  group: KanaGroup;
  order: number;     // gojūon position; default new-card sort key
}

/**
 * Per-device attempt counters (a G-counter cell): `t` = total attempts,
 * `c` = correct attempts. Only the owning device ever increments its own
 * bucket, which is what makes per-bucket max-merge exact across devices.
 */
export interface AttemptBucket {
  t: number;
  c: number;
}

export interface WordProgress {
  state: SRSState;
  lastReviewed: number | null;
  nextReview: number;
  correctStreak: number;
  /** Derived sum of `attempts[*].t` — kept flat for UI reads (stats, sorts). */
  totalAttempts: number;
  /** Derived sum of `attempts[*].c` — kept flat for UI reads. */
  correctAttempts: number;
  created: number;
  cardIndex: number;
  /**
   * Device-attributed attempt buckets, keyed by device id (see
   * services/deviceId). Reserved ids: `"legacy"` holds pre-migration flat
   * counts folded in lazily at merge/write time, and `"ephemeral"` is the
   * shared bucket for private-mode sessions where no id can persist.
   * Absent = pre-migration record (normalized on first merge or grade,
   * never eagerly rewritten on disk).
   */
  attempts?: Record<string, AttemptBucket>;
}

export interface WordProgressMap {
  [japanese: string]: WordProgress;
}

// ============================================
// Particle & Grammar Types
// ============================================

export interface ParticleExample {
  japanese: string;
  english: string;
  correct: string;
  explanation: string;
}

export interface Particle {
  particle: string;
  reading: string;
  function: string;
  description: string;
  examples: ParticleExample[];
}

// A single fill-in-the-blank grammar example. `japanese` carries one or more
// `___` blanks; `correct` is the answer (pipe-joined for multi-blank patterns,
// e.g. "ば|ほど"); `options` are the pre-authored answer choices (including the
// correct one).
export interface GrammarExample {
  japanese: string;
  english: string;
  correct: string;
  options: string[];
  explanation: string;
  difficulty?: string;
}

export interface GrammarPattern {
  pattern: string;
  reading: string;
  function: string;
  description: string;
  usageNotes?: string;
  jlptLevel?: string;
  category?: string;
  examples: GrammarExample[];
}

// ============================================
// Quiz Types
// ============================================

export interface QuizQuestion {
  question: Word;
  options: Word[];
  direction: 'jp-to-en' | 'en-to-jp';
}

export interface ParticleQuestion {
  particle: string;
  reading: string;
  function: string;
  description: string;
  example: ParticleExample;
  options: string[];
}

export interface GrammarQuestion {
  pattern: string;
  reading: string;
  function: string;
  description: string;
  example: GrammarExample;
  options: string[];
}

export type QuizOptionState = 'idle' | 'correct' | 'incorrect' | 'disabled';

// ============================================
// SRS Configuration Types
// ============================================

export interface SRSStateConfig {
  name: string;
  color: string;
  onColor: string;
  indicator: string;
  description: string;
}

export interface SRSStats {
  new: number;
  learning: number;
  review: number;
  mastered: number;
}

// ============================================
// Level Configuration
// ============================================

export interface LevelConfig {
  level: JLPTLevel;
  displayName: string;
  description: string;
  color: string;
  vocabulary: Word[];
  enabled: boolean;
}

export interface LevelInfo {
  level: JLPTLevel;
  displayName: string;
  description: string;
  color: string;
  wordCount: number;
  enabled: boolean;
}

// ============================================
// Error Types
// ============================================

export const ErrorType = {
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  NETWORK: 'NETWORK',
  RATE_LIMITED: 'RATE_LIMITED',
  PARSE_ERROR: 'PARSE_ERROR',
  TTS_FAILED: 'TTS_FAILED',
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
  INITIALIZATION_FAILED: 'INITIALIZATION_FAILED'
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

export interface AppError {
  type: ErrorType;
  message: string;
  details?: unknown;
  recoverable: boolean;
}

// ============================================
// Notification Types
// ============================================

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
}

// ============================================
// Audio Types
// ============================================

export interface AudioCacheEntry {
  blob: Blob;
  timestamp: number;
  text: string;
}

export interface TTSOptions {
  buttonElement?: HTMLElement;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

